// 설정 창 "계정" 묶음(D5a §9): 게스트/Google 연결됨 · 마지막 동기화 · [Google로 계정 연결]/[연결 해제] · [계정 삭제](화면 안 확인 상자).
// 브라우저 confirm/alert 대화상자는 쓰지 않는다. 설정 창 안에만 그리므로 플레이 화면을 가리지 않는다.
import { C, FONT } from '../ui/dom';
import type { AccountClient, AccountStatus } from './client';

const STATE_TEXT: Record<AccountStatus['state'], [string, string]> = {
  off: ['서버 저장 꺼짐', '#9fb0d8'],
  connecting: ['연결 중…', '#9fb0d8'],
  syncing: ['맞추는 중…', C.teal],
  synced: ['서버에 저장됨', '#7fe0a0'],
  pending: ['불러오기 대기', C.gold],
  offline: ['오프라인', '#ffb070'],
  'too-large': ['서버 보관 안 됨', '#ffb070'],
  relink: ['다시 연결 필요', '#ffb070'],
  error: ['저장 오류', '#ff8a8a'],
};

/** 마지막 동기화 시각 → "방금"·"3분 전"·"오후 3:12" */
export function syncAgo(at: number, now = Date.now()): string {
  if (!at) return '';
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 45) return '방금';
  if (s < 3600) return Math.max(1, Math.round(s / 60)) + '분 전';
  const d = new Date(at), h = d.getHours(), m = d.getMinutes();
  return (h < 12 ? '오전 ' : '오후 ') + ((h % 12) || 12) + ':' + (m < 10 ? '0' : '') + m;
}

function small(parent: HTMLElement, label: string, kind: 'gold' | 'plain' | 'danger', testId: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  b.dataset.testid = testId;
  const fill = kind === 'gold' ? 'background:linear-gradient(#ffd877,#e0a52f);color:#2a1d05;border:2px solid ' + C.gold + ';'
    : kind === 'danger' ? 'background:transparent;color:#ff8a8a;border:2px solid rgba(255,138,138,.55);'
    : 'background:linear-gradient(#26335a,#1A2238);color:' + C.gold + ';border:2px solid ' + C.gold + ';';
  b.style.cssText = 'pointer-events:auto;cursor:pointer;font:800 15px ' + FONT + ';padding:8px 14px;border-radius:10px;box-shadow:0 3px 0 rgba(0,0,0,.4);white-space:nowrap;' + fill;
  b.addEventListener('click', onClick);
  parent.appendChild(b);
  return b;
}
function div(parent: HTMLElement, css: string, text?: string): HTMLDivElement {
  const d = document.createElement('div');
  d.style.cssText = css;
  if (text !== undefined) d.textContent = text;
  parent.appendChild(d);
  return d;
}

export interface AccountSection { refresh(): void }

export function mountAccountSection(parent: HTMLElement, client: AccountClient): AccountSection {
  const box = div(parent, 'display:flex;flex-direction:column;gap:8px;padding:12px 14px;border:1px solid rgba(242,193,78,.45);border-radius:12px;background:rgba(10,16,34,.55);');
  box.dataset.testid = 'account-section';
  const head = div(box, 'display:flex;align-items:center;gap:10px;flex-wrap:wrap;');
  div(head, 'font:800 18px ' + FONT + ';color:#fff;', '계정');
  const badge = div(head, 'font:800 12px ' + FONT + ';padding:3px 9px;border-radius:999px;');
  badge.dataset.testid = 'account-badge';
  const state = div(head, 'font:700 12px ' + FONT + ';margin-left:auto;');
  state.dataset.testid = 'account-state';
  const desc = div(box, 'font:500 12px ' + FONT + ';color:#9fb0d8;line-height:1.45;');
  desc.dataset.testid = 'account-desc';
  const row = div(box, 'display:flex;gap:8px;flex-wrap:wrap;align-items:center;');
  const msg = div(box, 'display:none;font:600 12px ' + FONT + ';color:' + C.gold + ';line-height:1.45;');
  msg.dataset.testid = 'account-msg';
  let busy = false;
  const say = (t: string | null): void => { msg.textContent = t ?? ''; msg.style.display = t ? 'block' : 'none'; };
  const act = async (f: () => Promise<string | null>): Promise<void> => {
    if (busy) return;
    busy = true; render();
    try { say(await f()); } catch { say('잠시 뒤 다시 시도해 주세요'); }
    finally { busy = false; render(); }
  };
  const linkBtn = small(row, 'Google로 계정 연결', 'gold', 'account-google', () => void act(() => client.connectGoogle()));
  const unlinkBtn = small(row, '연결 해제', 'plain', 'account-unlink', () => { confirmUnlink.style.display = 'flex'; });
  const enableBtn = small(row, '서버 저장 켜기', 'gold', 'account-enable', () => { client.enable(); say(null); });
  const delBtn = small(row, '계정 삭제', 'danger', 'account-delete', () => { confirmDel.style.display = 'flex'; wipe.checked = false; });

  // 연결 해제 확인(화면 안)
  const confirmUnlink = div(box, 'display:none;flex-direction:column;gap:8px;padding:10px;border-radius:10px;background:rgba(0,0,0,.3);');
  div(confirmUnlink, 'font:600 13px ' + FONT + ';color:#e8eefc;line-height:1.45;', '연결을 해제하면 다른 기기에서 이 진행을 불러올 수 없어요. 이 기기에는 그대로 남아요.');
  const ur = div(confirmUnlink, 'display:flex;gap:8px;flex-wrap:wrap;');
  small(ur, '연결 해제', 'danger', 'account-unlink-confirm', () => { confirmUnlink.style.display = 'none'; void act(() => client.unlink()); });
  small(ur, '취소', 'plain', 'account-unlink-cancel', () => { confirmUnlink.style.display = 'none'; });

  // 계정 삭제 확인(화면 안, Play 정책): 로컬 유지가 기본
  const confirmDel = div(box, 'display:none;flex-direction:column;gap:8px;padding:10px;border-radius:10px;background:rgba(80,10,20,.35);border:1px solid rgba(255,138,138,.45);');
  confirmDel.dataset.testid = 'account-delete-box';
  div(confirmDel, 'font:700 13px ' + FONT + ';color:#ffd0d0;line-height:1.45;', '서버에 있는 계정·진행·연결 정보를 모두 지워요. 되돌릴 수 없어요.');
  const wl = document.createElement('label');
  wl.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;font:600 13px ' + FONT + ';color:#e8eefc;';
  const wipe = document.createElement('input');
  wipe.type = 'checkbox'; wipe.dataset.testid = 'account-delete-wipe';
  wipe.style.cssText = 'width:20px;height:20px;accent-color:#ff8a8a;';
  wl.append(wipe, document.createTextNode('이 기기의 진행도 지우기(처음부터 다시 시작)'));
  confirmDel.appendChild(wl);
  const dr = div(confirmDel, 'display:flex;gap:8px;flex-wrap:wrap;');
  small(dr, '삭제 확정', 'danger', 'account-delete-confirm', () => { confirmDel.style.display = 'none'; void act(() => client.deleteAccount(wipe.checked)); });
  small(dr, '취소', 'plain', 'account-delete-cancel', () => { confirmDel.style.display = 'none'; });

  function render(): void {
    const s = client.status;
    const google = s.provider === 'google';
    const off = s.state === 'off';
    badge.textContent = off ? '꺼짐' : google ? 'Google 연결됨' : s.provider === 'guest' ? '게스트' : '확인 중';
    badge.style.background = google ? 'rgba(79,182,208,.25)' : 'rgba(242,193,78,.18)';
    badge.style.color = google ? '#9fe6f7' : C.gold;
    const [label, color] = STATE_TEXT[s.state];
    const ago = s.state === 'synced' ? syncAgo(s.lastSyncAt) : '';
    state.textContent = '● ' + label + (ago ? ' · ' + ago : '');
    state.style.color = color;
    desc.textContent = s.note || (off ? '서버 저장이 꺼져 있어요. 켜면 새 계정에 지금 진행을 보관해요.'
      : google ? '다른 기기·브라우저에서 같은 Google로 연결하면 이 진행을 이어서 해요.'
      : '진행은 이 기기와 서버에 자동 저장돼요. Google로 연결하면 폰을 바꿔도 이어서 할 수 있어요.');
    linkBtn.style.display = !off && !google ? '' : 'none';
    unlinkBtn.style.display = !off && google ? '' : 'none';
    enableBtn.style.display = off || s.state === 'relink' ? '' : 'none';
    delBtn.style.display = !off && s.accountId ? '' : 'none';
    for (const b of [linkBtn, unlinkBtn, enableBtn, delBtn]) { b.disabled = busy; b.style.opacity = busy ? '0.5' : '1'; }
  }
  client.onChange(render);
  return { refresh: render };
}

/** 서버 진행을 불러온 뒤 한 번: 화면 위 가장자리 띠(플레이 영역을 가리지 않음, 입력 통과), 4초 뒤 사라짐 */
export function showRestoredToast(parent: HTMLElement): void {
  const t = document.createElement('div');
  t.dataset.testid = 'account-restored';
  t.textContent = '다른 기기의 진행을 불러왔어요';
  t.style.cssText = 'position:fixed;left:50%;top:calc(env(safe-area-inset-top, 0px) + 6px);transform:translateX(-50%);z-index:40;pointer-events:none;' +
    'font:800 14px ' + FONT + ';color:#2a1d05;background:linear-gradient(#ffd877,#e0a52f);padding:6px 16px;border-radius:999px;box-shadow:0 3px 10px rgba(0,0,0,.45);' +
    'transition:opacity .4s;opacity:1;white-space:nowrap;';
  parent.appendChild(t);
  setTimeout(() => { t.style.opacity = '0'; }, 4000);
  setTimeout(() => t.remove(), 4600);
}
